import React from 'react';
import { AlertCircle, ClipboardCheck } from 'lucide-react';
import { useUspsVetting } from '../hooks/useUspsVetting';
import { UspsGroupedRules } from './UspsReportBody';
import type { UspsVettingReport } from './uspsVetting.types';

/**
 * USPS Recommended Carrier Vetting (5-15-25), evaluated for this carrier.
 *
 * Every rule is shown with its own status. "Not evaluated" is deliberately loud: a rule we have no
 * data for is never counted as a pass, and the overall banner says "not a pass" while any remain.
 */

const BANNER: Record<UspsVettingReport['verdict']['status'], { box: string; title: string }> = {
  FAIL: { box: 'border-red-200 bg-red-50 text-red-800', title: 'Does not meet USPS vetting' },
  REVIEW: { box: 'border-yellow-200 bg-yellow-50 text-yellow-800', title: 'No failures found, but not all rules could be checked' },
  PASS: { box: 'border-green-200 bg-green-50 text-green-800', title: 'Meets USPS vetting' },
};

export const UspsVetting: React.FC<{ dotNumber: string }> = ({ dotNumber }) => {
  const { report, loading, error } = useUspsVetting(dotNumber);

  if (loading) return <div className="p-6 text-gray-600">Checking this carrier against the USPS rules…</div>;
  if (error || !report) {
    return (
      <div className="p-6">
        <div className="flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 p-4 text-red-700">
          <AlertCircle className="mt-0.5 h-5 w-5 shrink-0" />
          <div>
            <div className="font-medium">USPS vetting unavailable</div>
            <div className="text-sm">{error ?? 'No data returned.'}</div>
          </div>
        </div>
      </div>
    );
  }

  const banner = BANNER[report.verdict.status];

  return (
    <div className="space-y-6 p-6">
      <div>
        <h2 className="flex items-center gap-2 text-2xl font-semibold text-gray-900">
          <ClipboardCheck className="h-6 w-6" aria-hidden="true" />
          USPS Vetting
        </h2>
        <p className="mt-1 text-sm text-gray-600">{report.standard}. Each rule is checked against this carrier&apos;s data.</p>
      </div>

      <div className={`rounded-lg border p-4 ${banner.box}`} role="status">
        <div className="text-lg font-semibold">{banner.title}</div>
        <p className="mt-1 text-sm">{report.verdict.headline}</p>
        {report.verdict.autoFailsTriggered.length > 0 && (
          <ul className="mt-2 list-disc pl-5 text-sm font-medium">
            {report.verdict.autoFailsTriggered.map((t) => (
              <li key={t}>{t}</li>
            ))}
          </ul>
        )}
      </div>

      <UspsGroupedRules report={report} />

      <div className="rounded-lg border border-gray-200 bg-gray-50 p-4 text-sm text-gray-600">
        <ul className="list-disc space-y-1 pl-5">
          {report.notes.map((n) => (
            <li key={n}>{n}</li>
          ))}
        </ul>
        <p className="mt-2 text-xs text-gray-500">Computed {new Date(report.computedAt).toLocaleString()}. Results are cached for up to an hour.</p>
      </div>
    </div>
  );
};

export default UspsVetting;
